import { useState, useEffect } from 'react';
import { Loader2, RefreshCw, Zap, BarChart3, Newspaper, ShoppingBag, Clock, AlertTriangle, CheckCircle2, Database, Flame, Activity } from 'lucide-react';
import api from '@/api';

const axiosAdmin = api._axiosAdmin || null;

export function PipelineTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('wtf_admin_token');
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/admin/pipeline-logs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      setLogs(data.logs || []);
    } catch (e) {
      console.error('Failed to load pipeline logs:', e);
    } finally {
      setLoading(false);
    }
  };

  const triggerPipeline = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/pulse/run/all`, { method: 'POST' });
      const data = await resp.json();
      setRunResult(data);
      await loadLogs();
    } catch (e) {
      setRunResult({ error: e.message });
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { loadLogs(); }, []);

  return (
    <div data-testid="pipeline-tab" className="space-y-4">
      {/* Header + Trigger */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Pipeline Runs</h3>
          <p className="text-[10px] text-gray-400">Generation logs for all 3 engines</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadLogs} disabled={loading}
            className="px-3 py-1.5 text-[10px] font-bold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 flex items-center gap-1">
            {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Refresh
          </button>
          <button onClick={triggerPipeline} disabled={running} data-testid="trigger-pipeline-btn"
            className="px-3 py-1.5 text-[10px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1 disabled:opacity-50">
            {running ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
            {running ? 'Running...' : 'Run Now'}
          </button>
        </div>
      </div>

      {/* Live Run Result */}
      {runResult && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-2">Latest Run Result</p>
          {runResult.error ? (
            <p className="text-xs text-red-600">{runResult.error}</p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <StatBox icon={ShoppingBag} label="Traders" value={runResult.traders?.scored || 0} time={runResult.traders?.duration_s} color="emerald" />
              <StatBox icon={BarChart3} label="Investors" value={runResult.investors?.scored || 0} time={runResult.investors?.duration_s} color="blue" />
              <StatBox icon={Newspaper} label="News" value={runResult.news?.cards_generated || 0} time={runResult.news?.duration_s} color="amber" />
            </div>
          )}
          {runResult.total_duration_s && (
            <p className="text-[10px] text-indigo-500 mt-2 flex items-center gap-1">
              <Clock size={10} /> Total: {runResult.total_duration_s}s
              {runResult.errors?.length > 0 && <span className="text-red-500 ml-2">{runResult.errors.length} error(s)</span>}
            </p>
          )}
        </div>
      )}

      {/* Log History */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-300" size={24} /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-xs text-gray-400">No pipeline runs yet. Click "Run Now" to trigger.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log, i) => (
            <LogCard key={i} log={log} isLatest={i === 0} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatBox({ icon: Icon, label, value, time, color }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-2 ${colors[color]}`}>
      <div className="flex items-center gap-1 mb-1">
        <Icon size={10} />
        <span className="text-[9px] font-bold uppercase">{label}</span>
      </div>
      <p className="text-lg font-black">{value}</p>
      {time !== undefined && <p className="text-[9px] opacity-60">{time}s</p>}
    </div>
  );
}

function LogCard({ log, isLatest }) {
  const hasErrors = log.errors && log.errors.length > 0;
  const ranAt = log.ran_at ? new Date(log.ran_at).toLocaleString() : 'Unknown';
  const isTrends = log.pipeline_type === 'trends_v2';

  return (
    <div data-testid={`pipeline-log-${isLatest ? 'latest' : ''}`}
      className={`bg-white rounded-xl border ${isLatest ? 'border-indigo-200 ring-1 ring-indigo-100' : 'border-gray-100'} p-3`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {hasErrors
            ? <AlertTriangle size={12} className="text-amber-500" />
            : <CheckCircle2 size={12} className="text-green-500" />
          }
          <span className="text-[10px] font-bold text-gray-900">{ranAt}</span>
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase ${isTrends ? 'bg-violet-100 text-violet-600' : 'bg-sky-100 text-sky-600'}`}>
            {isTrends ? 'Trends v2' : 'Pulse'}
          </span>
          {isLatest && <span className="text-[8px] font-bold bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full uppercase">Latest</span>}
        </div>
        <span className="text-[10px] font-bold text-gray-500 flex items-center gap-1">
          <Clock size={10} /> {log.total_duration_s}s
        </span>
      </div>

      {/* Stats Grid — different shape per pipeline type */}
      {isTrends ? (
        <div className="grid grid-cols-3 gap-2 mb-2">
          <MiniStat icon={Activity} label="Signals" value={log.signals_generated} extra={`${log.entities_scored || 0} scored`} />
          <MiniStat icon={Database} label="Stored" value={log.entities_stored} extra="entities" />
          <MiniStat icon={Clock} label="Wall-clock" value={`${log.total_duration_s || 0}s`} extra="end-to-end" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 mb-2">
          <MiniStat icon={ShoppingBag} label="Traders" value={log.traders_scored} time={log.traders_duration_s} />
          <MiniStat icon={BarChart3} label="Investors" value={log.investors_scored} time={log.investors_duration_s} />
          <MiniStat icon={Newspaper} label="News" value={log.news_generated} time={log.news_duration_s} extra={log.news_processed ? `(${log.news_processed} processed)` : ''} />
        </div>
      )}

      {/* Bottleneck Report */}
      {log.bottlenecks && log.bottlenecks.length > 0 && (
        <BottleneckReport bottlenecks={log.bottlenecks} phaseTimings={log.phase_timings_s} />
      )}

      {/* OpenSearch (pulse only) */}
      {!isTrends && (
        <div className="flex items-center gap-3 text-[9px] text-gray-400 mt-2">
          <Database size={9} />
          <span>OS: {(log.opensearch_traders?.indexed || 0) + (log.opensearch_investors?.indexed || 0) + (log.opensearch_news?.indexed || 0)} indexed</span>
          {((log.opensearch_traders?.errors || 0) + (log.opensearch_investors?.errors || 0) + (log.opensearch_news?.errors || 0)) > 0 && (
            <span className="text-red-400">{(log.opensearch_traders?.errors || 0) + (log.opensearch_investors?.errors || 0) + (log.opensearch_news?.errors || 0)} errors</span>
          )}
        </div>
      )}

      {/* Errors */}
      {hasErrors && (
        <div className="mt-2 pt-2 border-t border-gray-50">
          {log.errors.slice(0, 5).map((err, i) => (
            <p key={i} className="text-[10px] text-red-500 flex items-start gap-1">
              <AlertTriangle size={9} className="mt-0.5 flex-shrink-0" /> {typeof err === 'string' ? err : JSON.stringify(err)}
            </p>
          ))}
          {log.errors.length > 5 && (
            <p className="text-[9px] text-gray-400 mt-1">+ {log.errors.length - 5} more</p>
          )}
        </div>
      )}
    </div>
  );
}

const PHASE_LABELS = {
  layer1_ingestion:   'Layer 1 — Ingestion',
  layer2_extraction:  'Layer 2 — Feature Extraction',
  layer3_embeddings:  'Layer 3 — Embeddings',
  layer4_matching:    'Layer 4 — kNN Vector Matching',
  layer5_scoring:     'Layer 5 — Score Engine (LLM)',
  layer6_output:      'Layer 6 — Storage',
  trader_pipeline:    'Trader sub-pipeline',
  investor_pipeline:  'Investor sub-pipeline',
  news_pipeline:      'News sub-pipeline',
};

function BottleneckReport({ bottlenecks, phaseTimings }) {
  const top = bottlenecks[0];
  const maxDur = Math.max(...bottlenecks.map(b => b.duration_s), 1);
  const rankColors = ['bg-orange-500', 'bg-orange-400', 'bg-orange-300'];
  const rankEmoji  = ['🔴 ', '🟠 ', '🟡 '];

  return (
    <div data-testid="bottleneck-report" className="mt-2 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-2 border border-orange-100">
      <div className="flex items-center gap-1 mb-1.5">
        <Flame size={10} className="text-orange-500" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-orange-700">Top bottlenecks</span>
        <span className="text-[9px] text-orange-500 ml-auto truncate max-w-[60%]">
          {(PHASE_LABELS[top.phase] || top.phase)} · {top.pct_of_total}% of wall-clock
        </span>
      </div>
      <div className="space-y-1">
        {bottlenecks.map((b, i) => (
          <div key={b.phase} className="flex items-center gap-2" data-testid={`bottleneck-row-${i}`}>
            <span className="text-[9px] font-semibold text-gray-700 w-44 truncate">
              {rankEmoji[i] || ''}{PHASE_LABELS[b.phase] || b.phase}
            </span>
            <div className="flex-1 h-2 bg-white rounded-full overflow-hidden border border-orange-100">
              <div
                className={`h-full ${rankColors[i] || 'bg-orange-200'}`}
                style={{ width: `${Math.max(4, 100 * b.duration_s / maxDur)}%` }}
              />
            </div>
            <span className="text-[9px] font-bold text-gray-600 tabular-nums w-16 text-right">
              {formatDuration(b.duration_s)}
            </span>
            <span className="text-[9px] font-semibold text-orange-600 tabular-nums w-10 text-right">
              {b.pct_of_total}%
            </span>
          </div>
        ))}
      </div>
      {phaseTimings && Object.keys(phaseTimings).length > bottlenecks.length && (
        <details className="mt-1.5" data-testid="bottleneck-all-phases">
          <summary className="text-[9px] text-gray-500 cursor-pointer hover:text-gray-700">
            + show all {Object.keys(phaseTimings).length} phases
          </summary>
          <div className="mt-1 space-y-0.5">
            {Object.entries(phaseTimings).sort((a, b) => b[1] - a[1]).map(([phase, dur]) => (
              <div key={phase} className="flex items-center justify-between text-[9px] text-gray-600">
                <span>{PHASE_LABELS[phase] || phase}</span>
                <span className="tabular-nums">{formatDuration(dur)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function formatDuration(s) {
  if (s == null) return '—';
  if (s >= 60) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  if (s >= 1)  return `${s.toFixed(1)}s`;
  return `${Math.round(s * 1000)}ms`;
}

function MiniStat({ icon: Icon, label, value, time, extra }) {
  const subline = time !== undefined
    ? `${time || 0}s${extra ? ` ${extra}` : ''}`
    : (extra || '');
  return (
    <div className="bg-gray-50 rounded-lg p-1.5">
      <div className="flex items-center gap-1">
        <Icon size={9} className="text-gray-400" />
        <span className="text-[8px] font-bold text-gray-500 uppercase">{label}</span>
      </div>
      <p className="text-sm font-black text-gray-900">{value ?? 0}</p>
      {subline && <p className="text-[8px] text-gray-400">{subline}</p>}
    </div>
  );
}
